import { useEffect } from 'react';

interface UseExportLoadingBridgeArgs {
  target: string;
  enabled: boolean;
}

const bodyStatusAttr = (target: string) => `data-export-status-${target.toLowerCase()}`;

/**
 * Holds `body[data-export-status-<target>]='loading'` for the orchestrator
 * while a parent component is still waiting on data, *without* clearing the
 * attribute when the bridge disables.
 *
 * This is the difference from {@link useExportSignal}: that hook's effect
 * cleanup removes the body status, which races with the actual emitter further
 * down the tree. React commits child effects before parent effects, so the
 * actual emitter sets `body[…]='ready'` first, then the parent bridge's
 * cleanup erases it. The orchestrator sees an empty status, falls through to
 * the missing-fallback timer, and marks the page "Skipped — target not
 * present" even though the content rendered fine.
 *
 * By only ever setting (never clearing), the bridge's value is harmlessly
 * overwritten by the real emitter when it takes over. If no real emitter ever
 * runs (e.g. ValuationApproaches never mounts), the orchestrator's stall
 * timer in `captureIframe.ts` is the long-stop.
 */
export const useExportLoadingBridge = ({ target, enabled }: UseExportLoadingBridgeArgs) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const { body } = document;
    if (body) {
      body.setAttribute(bodyStatusAttr(target), 'loading');
    }
  }, [target, enabled]);
};
